import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSponsorshipInterests,
  assignComponentSponsor,
  unassignComponentSponsorThunk,
  reviewSponsorshipInterest,
  updateInterestStatus,
} from "@/store/slices/adminSlice";
import Modal from "@/components/common/Modal";
import Button from "@/components/common/Button";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { toast } from "react-toastify";
import {
  Package, DollarSign, User, CheckCircle, XCircle,
  AlertCircle, ChevronDown, ChevronUp, Trash2, ClipboardCheck,
  ArrowRight, ArrowLeft, RefreshCw
} from "lucide-react";
import { getInterestStatusTailwind, getInterestStatusBadgeConfig, isInterestStatusFinal, getComponentLabel, INTEREST_STATUS_CONFIG } from "@/utils/constants";

// Status hierarchy order for determining forward vs backward transitions
const STATUS_ORDER = ['pending', 'reviewed', 'accepted', 'declined', 'partially_accepted'];
const STATUS_RANK = { pending: 0, reviewed: 1, accepted: 2, declined: 2, partially_accepted: 2 };

const isForwardTransition = (currentStatus, targetStatus) => {
  return (STATUS_RANK[targetStatus] ?? 0) > (STATUS_RANK[currentStatus] ?? 0);
};

const EventSponsorshipModal = ({ event, onClose }) => {
  const dispatch = useDispatch();
  const {
    sponsorshipData,
    isLoadingSponsorships,
    isUpdating,
  } = useSelector((state) => state.admin);

  const [confirmAction, setConfirmAction] = useState(null); // { type: 'assign'|'unassign', args: {} }
  const [expandedComponents, setExpandedComponents] = useState({});
  const [selectedSponsors, setSelectedSponsors] = useState({}); // { [componentId]: sponsorId }
  const [reviewingInterest, setReviewingInterest] = useState(null); // interest_id being reviewed
  const [statusChangeTarget, setStatusChangeTarget] = useState(null); // interest_id showing status options

  useEffect(() => {
    if (event?._id) {
      dispatch(fetchSponsorshipInterests(event._id));
    }
  }, [dispatch, event]);

  const toggleExpand = (id) => {
    setExpandedComponents((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleSponsorSelect = (componentId, sponsorId) => {
    setSelectedSponsors((prev) => ({
      ...prev,
      [componentId]: sponsorId,
    }));
  };

  const handleAssignClick = (component, sponsorId) => {
    const sponsor = component.interested_sponsors.find(s => s.sponsor_id === sponsorId)?.sponsor;
    setConfirmAction({
      type: "assign",
      label: `Assign "${sponsor?.company_name || 'Sponsor'}" to "${getComponentLabel(component.component_category)}"`,
      args: {
        eventId: event._id,
        componentId: component.component_id,
        sponsor_id: sponsorId,
      },
    });
  };

  const handleUnassignClick = (component) => {
    setConfirmAction({
      type: "unassign",
      label: `Unassign sponsor from "${getComponentLabel(component.component_category)}"`,
      args: {
        eventId: event._id,
        componentId: component.component_id,
      },
    });
  };

  const handleReview = async (interestId) => {
    try {
      const result = await dispatch(
        reviewSponsorshipInterest({ interestId })
      ).unwrap();
      toast.success(result.message || "Interest marked as reviewed");
      setReviewingInterest(null);
      // Refresh data to reflect changes
      dispatch(fetchSponsorshipInterests(event._id));
    } catch (error) {
      toast.error(error?.message || "Failed to review interest");
    }
  };

  const handleStatusChange = async (interestId, newStatus) => {
    try {
      const result = await dispatch(
        updateInterestStatus({ interestId, status: newStatus })
      ).unwrap();
      toast.success(result.message || `Status changed to ${getInterestStatusBadgeConfig(newStatus).label}`);
      setStatusChangeTarget(null);
      dispatch(fetchSponsorshipInterests(event._id));
    } catch (error) {
      toast.error(error?.message || "Failed to change status");
    }
  };

  const executeAction = async () => {
    if (!confirmAction) return;

    try {
      if (confirmAction.type === "assign") {
        await dispatch(assignComponentSponsor(confirmAction.args)).unwrap();
        toast.success("Sponsor assigned successfully");
         // Refresh data to reflect changes
        dispatch(fetchSponsorshipInterests(event._id));
      } else {
        await dispatch(unassignComponentSponsorThunk(confirmAction.args)).unwrap();
        toast.success("Sponsor unassigned successfully");
        // Refresh data
        dispatch(fetchSponsorshipInterests(event._id));
      }
    } catch (error) {
        // Error handled by slice/interceptor usually, but safe to toast here if needed
    } finally {
      setConfirmAction(null);
    }
  };

  if (!event) return null;

  return (
    <>
      <Modal
        open={true}
        onClose={onClose}
        title={`Sponsorship Management — ${event.title}`}
        maxWidth="max-w-4xl"
        showCloseButton={true}
      >
        <div className="space-y-6 min-h-[400px]">
          {isLoadingSponsorships ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-2"></div>
              Loading sponsorship details...
            </div>
          ) : !sponsorshipData ? (
             <div className="text-center py-12 text-gray-500">
                Failed to load data.
             </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg border border-gray-100 dark:border-gray-800">
                  <div className="flex gap-4">
                      <span>Total Components: <span className="font-semibold text-gray-900 dark:text-white">{sponsorshipData.component_summary?.length || 0}</span></span>
                      <span>Total Interests: <span className="font-semibold text-gray-900 dark:text-white">{sponsorshipData.interests?.length || 0}</span></span>
                  </div>
              </div>

              <div className="space-y-3">
                {sponsorshipData.component_summary?.map((comp) => {
                  const isExpanded = expandedComponents[comp.component_id];
                  const hasInterests = comp.interested_sponsors?.length > 0;
                  const selectedSponsorId = selectedSponsors[comp.component_id];

                  return (
                    <div
                      key={comp.component_id}
                      className={`border rounded-lg transition-all ${
                        comp.is_assigned
                          ? "border-green-200 dark:border-green-900/30 bg-green-50/30 dark:bg-green-900/10"
                          : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                      }`}
                    >
                      {/* Header Row */}
                      <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">
                              {getComponentLabel(comp.component_category)}
                            </h4>
                            {comp.is_assigned ? (
                              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" /> Assigned
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded-full">
                                Available
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                            {comp.component_description || "No description"}
                          </p>
                        </div>

                        <div className="flex items-center justify-between sm:justify-end gap-6 min-w-[200px]">
                           <div className="flex items-center gap-1 text-sm font-semibold text-gray-700 dark:text-gray-300">
                              <DollarSign className="w-4 h-4 text-gray-400" />
                              {comp.component_value?.toLocaleString("en-IN") || 0}
                           </div>

                           <div className="text-sm text-right">
                              {comp.is_assigned ? (
                                <div className="flex items-center gap-2">
                                    <div className="text-right">
                                        <p className="text-xs text-gray-500">Assigned to</p>
                                        <p className="font-medium text-gray-900 dark:text-white truncate max-w-[150px]">
                                            {comp.assigned_sponsor?.company_name}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => handleUnassignClick(comp)}
                                        disabled={isUpdating}
                                        className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                        title="Unassign Sponsor"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                              ) : (
                                <div className="text-right">
                                     <p className="text-xs text-gray-500">Interests</p>
                                     <p className="font-medium text-gray-900 dark:text-white">
                                         {comp.interest_count} sponsor{comp.interest_count !== 1 ? 's' : ''}
                                     </p>
                                </div>
                              )}
                           </div>
                        </div>
                        
                        <button
                            onClick={() => toggleExpand(comp.component_id)}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        >
                            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>
                      </div>

                      {/* Expanded Section */}
                      {isExpanded && (
                        <div className="px-4 pb-4 pt-0 border-t border-gray-100 dark:border-gray-700/50 mt-2">
                            <div className="mt-3">
                                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                                    <User className="w-4 h-4" /> Interested Sponsors
                                </h5>
                                
                                {comp.interested_sponsors?.length === 0 ? (
                                    <p className="text-sm text-gray-500 italic px-2">No sponsors have expressed interest in this component yet.</p>
                                ) : (
                                    <div className="space-y-2">
                                        {comp.interested_sponsors.map((interest) => (
                                            <div key={interest.interest_id} className="p-3 bg-gray-50 dark:bg-gray-900/30 rounded-lg border border-gray-100 dark:border-gray-800">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                            {interest.sponsor?.company_name}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {interest.sponsor?.email}
                                                        </p>
                                                        {interest.message && (
                                                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 italic">
                                                                "{interest.message}"
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                         <span className={`text-xs px-2 py-1 rounded capitalize ${getInterestStatusTailwind(interest.interest_status)}`}>
                                                             {getInterestStatusBadgeConfig(interest.interest_status).label}
                                                         </span>
                                                         {!isInterestStatusFinal(interest.interest_status) && (
                                                            <Button
                                                               size="sm"
                                                               variant="outline"
                                                               disabled={isUpdating}
                                                               onClick={() => {
                                                                 setReviewingInterest(
                                                                   reviewingInterest === interest.interest_id ? null : interest.interest_id
                                                                 );
                                                               }}
                                                               className="text-xs px-3 py-1.5 h-auto flex items-center gap-1"
                                                            >
                                                                <ClipboardCheck className="w-3.5 h-3.5" />
                                                                Review
                                                            </Button>
                                                         )}
                                                         <Button
                                                            size="sm"
                                                            variant="outline"
                                                            disabled={isUpdating}
                                                            onClick={() => {
                                                              setStatusChangeTarget(
                                                                statusChangeTarget === interest.interest_id ? null : interest.interest_id
                                                              );
                                                              setReviewingInterest(null);
                                                            }}
                                                            className="text-xs px-3 py-1.5 h-auto flex items-center gap-1"
                                                         >
                                                             <RefreshCw className="w-3.5 h-3.5" />
                                                             Status
                                                         </Button>
                                                         {!comp.is_assigned && (
                                                             <Button
                                                                size="sm"
                                                                variant="primary"
                                                                disabled={isUpdating}
                                                                onClick={() => handleAssignClick(comp, interest.sponsor_id)}
                                                                className="text-xs px-3 py-1.5 h-auto"
                                                             >
                                                                 Assign
                                                             </Button>
                                                         )}
                                                    </div>
                                                </div>

                                                {/* Review Confirmation (inline) */}
                                                {reviewingInterest === interest.interest_id && (
                                                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                                                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                                                      Marking as reviewed will send a standard notification to the sponsor.
                                                    </p>
                                                    <div className="flex justify-end gap-2">
                                                      <Button
                                                        size="sm"
                                                        variant="outline"
                                                        type="button"
                                                        onClick={() => setReviewingInterest(null)}
                                                        className="text-xs px-3 py-1.5 h-auto"
                                                      >
                                                        Cancel
                                                      </Button>
                                                      <Button
                                                        size="sm"
                                                        variant="primary"
                                                        disabled={isUpdating}
                                                        loading={isUpdating}
                                                        onClick={() => handleReview(interest.interest_id)}
                                                        className="text-xs px-3 py-1.5 h-auto bg-purple-600 hover:bg-purple-700"
                                                      >
                                                        Confirm Review
                                                      </Button>
                                                    </div>
                                                  </div>
                                                )}

                                                {/* Status Change Panel */}
                                                {statusChangeTarget === interest.interest_id && (
                                                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                                                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-1">
                                                      <RefreshCw className="w-3 h-3" />
                                                      Change Status
                                                    </p>
                                                    {(() => {
                                                      const currentStatus = interest.interest_status;
                                                      const allStatuses = Object.keys(INTEREST_STATUS_CONFIG);
                                                      const otherStatuses = allStatuses.filter(s => s !== currentStatus);
                                                      const forward = otherStatuses.filter(s => isForwardTransition(currentStatus, s));
                                                      const backward = otherStatuses.filter(s => !isForwardTransition(currentStatus, s));

                                                      return (
                                                        <div className="space-y-2">
                                                          {/* Forward transitions */}
                                                          {forward.length > 0 && (
                                                            <div>
                                                              <p className="text-[10px] uppercase tracking-wider font-semibold text-green-600 dark:text-green-400 mb-1 flex items-center gap-1">
                                                                <ArrowRight className="w-3 h-3" /> Advance
                                                              </p>
                                                              <div className="flex flex-wrap gap-1.5">
                                                                {forward.map(s => (
                                                                  <button
                                                                    key={s}
                                                                    disabled={isUpdating}
                                                                    onClick={() => handleStatusChange(interest.interest_id, s)}
                                                                    className="text-xs px-2.5 py-1.5 rounded-md border font-medium transition-all bg-green-50 border-green-200 text-green-700 hover:bg-green-100 hover:border-green-300 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400 dark:hover:bg-green-900/40 disabled:opacity-50"
                                                                  >
                                                                    {getInterestStatusBadgeConfig(s).label}
                                                                  </button>
                                                                ))}
                                                              </div>
                                                            </div>
                                                          )}

                                                          {/* Backward transitions */}
                                                          {backward.length > 0 && (
                                                            <div>
                                                              <p className="text-[10px] uppercase tracking-wider font-semibold text-amber-600 dark:text-amber-400 mb-1 flex items-center gap-1">
                                                                <ArrowLeft className="w-3 h-3" /> Backtrack
                                                              </p>
                                                              <div className="flex flex-wrap gap-1.5">
                                                                {backward.map(s => (
                                                                  <button
                                                                    key={s}
                                                                    disabled={isUpdating}
                                                                    onClick={() => handleStatusChange(interest.interest_id, s)}
                                                                    className="text-xs px-2.5 py-1.5 rounded-md border font-medium transition-all bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100 hover:border-amber-300 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-400 dark:hover:bg-amber-900/40 disabled:opacity-50"
                                                                  >
                                                                    {getInterestStatusBadgeConfig(s).label}
                                                                  </button>
                                                                ))}
                                                              </div>
                                                            </div>
                                                          )}

                                                          <div className="flex justify-end pt-1">
                                                            <Button
                                                              size="sm"
                                                              variant="outline"
                                                              type="button"
                                                              onClick={() => setStatusChangeTarget(null)}
                                                              className="text-xs px-3 py-1 h-auto"
                                                            >
                                                              Cancel
                                                            </Button>
                                                          </div>
                                                        </div>
                                                      );
                                                    })()}
                                                  </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={!!confirmAction}
        title={confirmAction?.type === "assign" ? "Confirm Assignment" : "Confirm Unassignment"}
        message={
            confirmAction?.type === "assign" 
             ? `Are you sure you want to assign this sponsor? They will be linked to this component publicly.`
             : `Are you sure you want to remove the assigned sponsor? This component will become available again.`
        }
        confirmLabel={confirmAction?.type === "assign" ? "Assign Sponsor" : "Unassign Sponsor"}
        confirmVariant={confirmAction?.type === "assign" ? "primary" : "danger"}
        onClose={() => setConfirmAction(null)}
        onConfirm={executeAction}
        isLoading={isUpdating}
      />
    </>
  );
};

export default EventSponsorshipModal;
